'use client'

import * as React from 'react'

import { cn } from '../cn'
import { Button } from './button'

/**
 * Composer primitives — the building blocks every Ordo chat composer
 * shares: outer shell, auto-growing textarea (with Safari fallback),
 * action row, icon buttons, send button. Lifted from
 * `apps/agent/client/src/components/chat/composer-primitives.tsx`.
 *
 * Density preset:
 *  - `comfortable` (default) — home/hero composers; 40px buttons
 *  - `compact`               — in-conversation chat; 36px buttons
 *
 * Vertical padding for the textarea wrapper is owned by the call site
 * (so each surface tunes its own breathing room); these primitives
 * only style the action-row + button density.
 */
export type ComposerDensity = 'comfortable' | 'compact'

export function ComposerShell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-card shadow-sm focus-within:border-foreground/30 transition-colors',
        className,
      )}>
      {children}
    </div>
  )
}

export const ComposerTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 1, onInput, ...props }, ref) => {
  const internalRef = React.useRef<HTMLTextAreaElement>(null)

  // Merge the internal ref with the forwarded ref so callers keep
  // working — chat surfaces commonly call `.focus()` / `.select()` on
  // the forwarded ref.
  React.useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement, [])

  // JS auto-grow fallback for Safari. Chrome honors `field-sizing:content`
  // (when set), so the resize is a no-op there. Safari (through v18) does
  // not implement `field-sizing`, so without this the textarea stays at
  // rows={1} height regardless of content.
  const resize = React.useCallback(() => {
    const el = internalRef.current
    if (!el) {
      return
    }
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [])

  React.useLayoutEffect(() => {
    resize()
  })

  return (
    <textarea
      ref={internalRef}
      rows={rows}
      {...props}
      onInput={(e) => {
        resize()
        onInput?.(e)
      }}
      className={cn(
        // text-base (16px) is the iOS-zoom-on-tap floor — do not lower.
        'block w-full resize-none border-0 bg-transparent px-4 py-0 text-base leading-relaxed outline-none placeholder:text-muted-foreground/60 focus:outline-none focus-visible:outline-none',
        'max-h-[40vh] overflow-y-auto',
        className,
      )}
    />
  )
})
ComposerTextarea.displayName = 'ComposerTextarea'

export function ComposerActionRow({
  left,
  right,
  density = 'comfortable',
}: {
  left?: React.ReactNode
  right: React.ReactNode
  density?: ComposerDensity
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-t border-border/40 px-2.5',
        density === 'compact' ? 'py-1.5' : 'py-2',
      )}>
      {left}
      <div className="flex-1" />
      {right}
    </div>
  )
}

type ButtonProps = React.ComponentProps<typeof Button> & { density?: ComposerDensity }

export const ComposerIconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'ghost', density = 'comfortable', ...props }, ref) => (
    <Button
      ref={ref}
      type="button"
      size="icon"
      variant={variant}
      {...props}
      className={cn(
        'shrink-0',
        density === 'compact' ? 'h-9 w-9 [&>svg]:!h-4 [&>svg]:!w-4' : 'h-10 w-10 [&>svg]:!h-5 [&>svg]:!w-5',
        className,
      )}
    />
  ),
)
ComposerIconButton.displayName = 'ComposerIconButton'

export const ComposerSendButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, density = 'comfortable', ...props }, ref) => (
    <Button
      ref={ref}
      type="button"
      size="icon"
      {...props}
      className={cn(
        'rounded-full shrink-0',
        density === 'compact' ? 'h-9 w-9 [&>svg]:!h-4 [&>svg]:!w-4' : 'h-10 w-10 [&>svg]:!h-5 [&>svg]:!w-5',
        className,
      )}
    />
  ),
)
ComposerSendButton.displayName = 'ComposerSendButton'
